/**
 * ConnectorCategorySections
 *
 * Shared rendering for the Advertising / Revenue / Forms sections of the Data Sources
 * page. Iterates `getConnectorsByCategory()` from the single source-of-truth config so
 * both the workspace-scoped and property-scoped Connectors pages stay in lockstep when
 * connectors are added or their lifecycle flips.
 *
 * Each page passes a `resolver` that maps a connector ID → live status (OAuth state,
 * credential connected, etc). Coming-soon connectors bypass the resolver and render
 * with the Coming Soon badge automatically.
 *
 * Website Traffic section is intentionally NOT unified here — the two pages render the
 * Humblytics Script row differently (workspace lists per-property tracking status;
 * property has a single row). Each page keeps its own Website Traffic block.
 */

import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { DataSourceRow, type DataSourceStatus } from '@/components/connectors/DataSourceRow'
import { getConnectorsByCategory } from '@/lib/config/connectors'
import type { ConnectorConfig } from '@/lib/types/connector'

interface ConnectorStatusResolution {
  status: DataSourceStatus
  subtitle?: string
  onClick?: () => void
  onSecondaryAction?: () => void
  secondaryActionLabel?: string
}

/** Per-page callback: given a connector ID, return its live status. */
type ConnectorStatusResolver = (connectorId: string) => ConnectorStatusResolution | null

interface CategorySectionProps {
  title: string
  category: ConnectorConfig['category']
  resolver: ConnectorStatusResolver
  isLoading?: boolean
  /** Connector IDs to exclude (e.g. Humblytics tracking is rendered separately). */
  excludeIds?: string[]
  /** Eyebrow meta; defaults to the connector count. */
  meta?: string
}

/** Paper "Connectors": 13px title + muted meta, then a gray well holding one raised card of hairline-divided rows. */
export function ConnectorSection({ title, meta, children }: { title: string; meta?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-baseline gap-2">
        <h2 className="text-[13px] font-semibold leading-4 text-foreground">{title}</h2>
        {meta && <span className="text-xs leading-4 text-foreground/60">{meta}</span>}
      </div>
      <div className="well">
        <div className="surface-raised flex flex-col overflow-clip [&>*+*]:border-t [&>*+*]:border-black/[0.08] dark:[&>*+*]:border-white/[0.08]">
          {children}
        </div>
      </div>
    </section>
  )
}

function CategorySection({ title, category, resolver, isLoading, excludeIds, meta }: CategorySectionProps) {
  const connectors = getConnectorsByCategory(category)
    .filter((c) => !excludeIds?.includes(String(c.id)))

  if (connectors.length === 0) return null

  return (
    <ConnectorSection title={title} meta={meta}>
          {isLoading ? (
            <div className="flex items-center gap-3.5 px-[18px] py-4">
              <Skeleton className="size-9 rounded-[9px]" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-56" />
              </div>
            </div>
          ) : (
            connectors.map((c) => {
              const r = resolver(String(c.id))
              // Coming-soon connectors render with the badge unless there's a live
              // connection (a legacy/test connection should keep showing real state).
              if (c.comingSoon && !r) {
                return (
                  <DataSourceRow
                    key={String(c.id)}
                    name={c.name}
                    description={c.description}
                    brand={c.brandKey}
                    status="coming-soon"
                  />
                )
              }
              if (!r) return null
              return (
                <DataSourceRow
                  key={String(c.id)}
                  name={c.name}
                  description={c.description}
                  brand={c.brandKey}
                  status={r.status}
                  subtitle={r.subtitle}
                  onClick={r.onClick}
                  onSecondaryAction={r.onSecondaryAction}
                  secondaryActionLabel={r.secondaryActionLabel}
                />
              )
            })
          )}
    </ConnectorSection>
  )
}

interface Props {
  resolver: ConnectorStatusResolver
  isLoading?: boolean
  workspaceName?: string
}

export function ConnectorCategorySections({ resolver, isLoading, workspaceName }: Props) {
  const scope = workspaceName ? ` · shared by ${workspaceName}` : ''
  return (
    <>
      <CategorySection title="Ad platforms" category="advertising" resolver={resolver} isLoading={isLoading} meta={`spend and campaigns${scope}`} />
      <CategorySection title="Payments" category="revenue" resolver={resolver} isLoading={isLoading} meta={`revenue and subscriptions${scope}`} />
      <CategorySection title="Form tools" category="forms" resolver={resolver} meta="submissions from embedded forms" />
    </>
  )
}
